// src/hooks/useBusinessCurrency.tsx
import { useState, useEffect } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import currencies from '@/pages/personal/settings/SettingsSection/currencies.json'; // Импортируем список валют из вашего JSON файла

// Функция для безопасного доступа к localStorage
const safelyGetItem = (key: string): string | null => {
  try {
    const value = localStorage.getItem(key);
    return value === 'undefined' ? null : value;
  } catch (e) {
    console.error(`Error getting localStorage item ${key}:`, e);
    return null;
  }
};

const safelySetItem = (key: string, value: string | null): void => {
  try {
    if (value && value !== 'undefined') {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error(`Error setting localStorage item ${key}:`, e);
  }
};

// Интерфейс для валюты
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  translation_key: string;
}

// Интерфейс возвращаемого значения хука
export interface BusinessCurrencyResult {
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
  formatPrice: (amount: number | string, maxFractionDigits?: number) => string;
}

export const useBusinessCurrency = (): BusinessCurrencyResult => {
  const { user, isAuthenticated } = useAuthContext();
  const [currencyCode, setCurrencyCode] = useState<string>('KZT'); // Значение по умолчанию
  const [currencyDetails, setCurrencyDetails] = useState<Currency | null>(null);

  // Подписываемся на событие изменения настроек
  useEffect(() => {
    const handleSettingsChanged = () => {
      const savedCurrency = safelyGetItem('businessCurrency');
      if (savedCurrency) {
        setCurrencyCode(savedCurrency);
      }
    };

    // Создаем пользовательское событие для оповещения об изменении настроек
    if (typeof window !== 'undefined') {
      window.addEventListener('business:settings:updated', handleSettingsChanged);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('business:settings:updated', handleSettingsChanged);
      }
    };
  }, []);

  // Получаем валюту из контекста авторизации при изменении пользователя
  useEffect(() => {
    if (isAuthenticated && user && user.business_currency) {
      setCurrencyCode(user.business_currency);
      safelySetItem('businessCurrency', user.business_currency);
    } else if (!isAuthenticated) {
      // Если пользователь не авторизован, используем значение из localStorage или по умолчанию
      const savedCurrency = safelyGetItem('businessCurrency');
      if (savedCurrency) {
        setCurrencyCode(savedCurrency);
      }
    }
  }, [user, isAuthenticated]);

  // Находим детали валюты по коду
  useEffect(() => {
    const currencyObj = currencies.find(c => c.code === currencyCode);
    if (currencyObj) {
      setCurrencyDetails(currencyObj);
    }
  }, [currencyCode]);

  // Функция для форматирования цены с учетом выбранной валюты
  const formatPrice = (amount: number | string, maxFractionDigits?: number): string => {
    if (!currencyDetails) return `${amount}`;
    
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return `0 ${currencyDetails.symbol}`;
    
    // Определение количества десятичных знаков в зависимости от величины числа
    const getAdaptiveMaxFractionDigits = (num: number): number => {
      const absNum = Math.abs(num);
      if (absNum >= 1000) return 0;  // Для сумм >= 1000 округляем до целых
      if (absNum >= 100) return 1;   // Для сумм >= 100 показываем 1 знак после запятой
      return 2;                       // Для меньших сумм показываем до 2 знаков
    };
    
    // Используем переданное значение или вычисляем автоматически
    const fractionDigits = maxFractionDigits !== undefined
      ? maxFractionDigits
      : getAdaptiveMaxFractionDigits(numericAmount);
    
    // Форматируем число с учетом локали и добавляем символ валюты
    const formattedNumber = numericAmount.toLocaleString('ru-RU', {
      minimumFractionDigits: fractionDigits === 0 ? 0 : 1, // Минимум 1 знак, если не нулевые дроби
      maximumFractionDigits: fractionDigits  // Динамическое количество знаков
    });
    
    return `${formattedNumber} ${currencyDetails.symbol}`;
  };

  return {
    currencyCode,
    currencySymbol: currencyDetails?.symbol || '₸',
    currencyName: currencyDetails?.name || 'Казахстанский тенге',
    formatPrice
  };
};

export default useBusinessCurrency;